import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { useState, useEffect } from 'react'

// Import screens
import LandingScreen from './screens/LandingScreen'
import OnboardingScreen from './screens/OnboardingScreen'
import HomeScreen from './screens/HomeScreen'
import ModuleScreen from './screens/ModuleScreen'
import LessonScreen from './screens/LessonScreen'
import ScoutLessonScreen from './screens/ScoutLessonScreen'
import SimulationScreen from './screens/SimulationScreen'
import SimulationHub from './screens/SimulationHub'
import PersonalityQuiz from './screens/PersonalityQuiz'
import TwoPlayerSimulation from './screens/TwoPlayerSimulation'
import QuizScreen from './screens/QuizScreen'
import BadgeScreen from './screens/BadgeScreen'
import EmergencyMode from './screens/EmergencyMode'
import AuthScreen from './screens/AuthScreen'
import NotFoundScreen from './screens/NotFoundScreen'
import DisasterPathsScreen from './screens/DisasterPathsScreen'
import PathDetailScreen from './screens/PathDetailScreen'
import PricingScreen from './screens/PricingScreen'
import ForgotPasswordScreen from './screens/ForgotPasswordScreen'
import ResetPasswordScreen from './screens/ResetPasswordScreen'

// Import components
import ProtectedRoute from './components/ProtectedRoute'
import ChaosMode, { useChaosMode } from './components/ChaosMode'
import ErrorBoundary, { APIErrorBoundary } from './components/ErrorBoundary'
import Footer from './components/Footer'

// Import providers
import { AuthProvider } from './contexts/AuthContext'
import { SubscriptionProvider } from './contexts/SubscriptionContext'

// Import utilities
import logger from './utils/logger'

// Import global styles
import './styles/globals.css'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep cache for 10 minutes
      cacheTime: 10 * 60 * 1000,
      // Retry failed requests 3 times
      retry: 3,
      // Enable refetch on window focus for fresh data
      refetchOnWindowFocus: true,
    },
  },
})

/**
 * App content that needs to access location
 */
function AppContent() {
  const location = useLocation()
  
  // Chaos Mode state
  const { enabled: chaosModeEnabled } = useChaosMode()
  
  return (
    <div className="app">
      {/* Development Banner */}
      <div className="dev-banner">
        🚧 SurviveIt is currently under development - Coming Soon!
      </div>

      {/* Main app routes with top padding to account for banner */}
      <div className="app-content">
        <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingScreen />} />
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/auth" element={<AuthScreen />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/pricing" element={<PricingScreen />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <HomeScreen />
          </ProtectedRoute>
        } />
        <Route path="/module/:moduleId" element={
          <ProtectedRoute>
            <ModuleScreen />
          </ProtectedRoute>
        } />
        <Route path="/lesson/:lessonId" element={
          <ProtectedRoute>
            <LessonScreen />
          </ProtectedRoute>
        } />
        <Route path="/lessons/:slug" element={
          <ProtectedRoute>
            <ScoutLessonScreen />
          </ProtectedRoute>
        } />
        <Route path="/simulations" element={
          <ProtectedRoute>
            <SimulationHub />
          </ProtectedRoute>
        } />
        <Route path="/simulation/:simulationId" element={
          <ProtectedRoute>
            <SimulationScreen />
          </ProtectedRoute>
        } />
        <Route path="/2player/:simulationId/:sessionId?" element={
          <ProtectedRoute>
            <TwoPlayerSimulation />
          </ProtectedRoute>
        } />
        <Route path="/quiz/:lessonId" element={
          <ProtectedRoute>
            <QuizScreen />
          </ProtectedRoute>
        } />
        <Route path="/badges" element={
          <ProtectedRoute>
            <BadgeScreen />
          </ProtectedRoute>
        } />
        <Route path="/paths" element={
          <ProtectedRoute>
            <DisasterPathsScreen />
          </ProtectedRoute>
        } />
        <Route path="/paths/:slug" element={
          <ProtectedRoute>
            <PathDetailScreen />
          </ProtectedRoute>
        } />

        {/* Personality Quiz - accessible without auth for virality */}
        <Route path="/personality-quiz" element={<PersonalityQuiz />} />
        
        {/* Emergency mode - accessible without auth for emergency situations */}
        <Route path="/emergency" element={<EmergencyMode />} />
        
        {/* 404 Not Found page */}
        <Route path="*" element={<NotFoundScreen />} />
        </Routes>

        {/* Emergency button - always visible except in emergency mode */}
        {location.pathname !== '/emergency' && (
          <button 
            className="emergency-float-button"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/emergency'
              }
            }}
            aria-label="Emergency Mode"
          >
            🚨
          </button>
        )}

        {/* Chaos Mode - only show when user is authenticated */}
        {location.pathname !== '/auth' && 
         location.pathname !== '/personality-quiz' && 
         chaosModeEnabled && (
          <ChaosMode enabled={true} minDelay={60000} maxDelay={180000} />
        )}

        {/* Toast notifications */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#333',
              color: '#fff',
            },
            success: {
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />

        {/* Global Footer - appears on all pages except landing and onboarding */}
        {location.pathname !== '/' && location.pathname !== '/onboarding' && <Footer />}
      </div>
    </div>
  )
}

/**
 * Main App Component
 * Sets up routing, global state management, and app-wide features
 */
function App() {
  // Track if app is installed as PWA
  const [_isInstalled, setIsInstalled] = useState(false)
  
  // Track online/offline status
  const [isOnline, setIsOnline] = useState(true)

  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return

    // Set initial online status
    setIsOnline(navigator.onLine)

    // Initialize logger
    logger.info('App Started', {
      isInstalled: window.matchMedia('(display-mode: standalone)').matches,
      isOnline: navigator.onLine,
      userAgent: navigator.userAgent
    })

    // Check if app is installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
      logger.info('PWA Mode Detected')
    }

    // Handle online/offline events
    const handleOnline = () => {
      setIsOnline(true)
      logger.info('App came online')
    }
    const handleOffline = () => {
      setIsOnline(false)
      logger.warn('App went offline')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Clean up event listeners
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SubscriptionProvider>
            <Router>
              <div data-online={isOnline}>
                {/* Show offline indicator */}
                {!isOnline && (
                  <div className="offline-banner">
                    <span>You're offline - some features may be limited</span>
                  </div>
                )}

                <AppContent />
              </div>
            </Router>
          </SubscriptionProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
